import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { EM_DASH, cn } from "@/lib/utils";

import {
  CORRECTIVE_ACTIONS_LIST_KEY,
  CORRECTIVE_ACTION_SOURCES,
  CORRECTIVE_ACTION_STATUSES,
  correctiveActionSourceLabel,
  correctiveActionStatusLabel,
  createCorrectiveAction,
  fetchCorrectiveActions,
  type CorrectiveActionReport,
  type CorrectiveActionSource,
  type CorrectiveActionStatus,
} from "./correctiveActionsApi";
import { QualitySalesOrderPicker } from "./QualitySalesOrderPicker";

const HELP_TEXT = [
  "Corrective action reports (CAR) follow ISO 9001 clause 10.2: document the nonconformity, immediate containment,",
  "root cause, corrective action, effectiveness verification, and preventive measures.",
  "",
  "Use source type RMA or customer complaint for field returns; link a sales order, QC incident, or device serial when",
  "the CAR traces back to manufacturing records.",
].join(" ");

type OpenStatusFilter = CorrectiveActionStatus | "open_workflow";

const OPEN_WORKFLOW_STATUSES = new Set<CorrectiveActionStatus>([
  "draft",
  "open",
  "containment",
  "analysis",
  "action",
  "verification",
]);

const STATUS_FILTER_OPTIONS: Array<{ key: OpenStatusFilter | "all"; label: string }> = [
  { key: "all", label: "All" },
  { key: "open_workflow", label: "Open workflow" },
  ...CORRECTIVE_ACTION_STATUSES.map((status) => ({
    key: status,
    label: correctiveActionStatusLabel(status),
  })),
];

function statusTone(status: CorrectiveActionStatus): BadgeTone {
  switch (status) {
    case "closed":
      return "success";
    case "cancelled":
      return "neutral";
    case "verification":
      return "info";
    case "draft":
      return "neutral";
    default:
      return "warning";
  }
}

function formatDate(value: string | null): string {
  if (!value) return EM_DASH;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const CAR_COLUMNS: HubDataTableColumn<CorrectiveActionReport>[] = [
  {
    id: "carNumber",
    header: "CAR",
    sortKey: "carNumber",
    overflow: "nowrap",
    sortValue: (row) => row.carNumber,
    cell: (row) => (
      <Link className="font-medium text-sky-700 hover:underline" to={`/quality/corrective-actions/${row.id}`}>
        {row.carNumber}
      </Link>
    ),
  },
  {
    id: "title",
    header: "Title",
    sortKey: "title",
    sortValue: (row) => row.title,
    cell: (row) => row.title,
  },
  {
    id: "status",
    header: "Status",
    sortKey: "status",
    sortValue: (row) => row.status,
    cell: (row) => <Badge tone={statusTone(row.status)}>{correctiveActionStatusLabel(row.status)}</Badge>,
  },
  {
    id: "sourceType",
    header: "Source",
    sortKey: "sourceType",
    sortValue: (row) => row.sourceType,
    cell: (row) => correctiveActionSourceLabel(row.sourceType),
  },
  {
    id: "owner",
    header: "Owner",
    sortKey: "owner",
    sortValue: (row) => row.ownerDisplay ?? "",
    cell: (row) => row.ownerDisplay ?? EM_DASH,
  },
  {
    id: "openedAt",
    header: "Opened",
    sortKey: "openedAt",
    overflow: "nowrap",
    sortValue: (row) => row.openedAt,
    cell: (row) => formatDate(row.openedAt.slice(0, 10)),
  },
  {
    id: "targetCloseDate",
    header: "Target close",
    sortKey: "targetCloseDate",
    overflow: "nowrap",
    sortValue: (row) => row.targetCloseDate ?? "",
    cell: (row) => formatDate(row.targetCloseDate),
  },
];

export function CorrectiveActionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<OpenStatusFilter | "all">("open_workflow");
  const [sourceFilter, setSourceFilter] = useState<CorrectiveActionSource | "all">("all");
  const [showCreate, setShowCreate] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createSource, setCreateSource] = useState<CorrectiveActionSource>("rma");
  const [createProblem, setCreateProblem] = useState("");
  const [createContainment, setCreateContainment] = useState("");
  const [createTargetCloseDate, setCreateTargetCloseDate] = useState("");
  const [createSalesOrderId, setCreateSalesOrderId] = useState<number | null>(null);
  const [createQualityIncidentId, setCreateQualityIncidentId] = useState("");

  const listQuery = useQuery({
    queryKey: [...CORRECTIVE_ACTIONS_LIST_KEY, search, statusFilter, sourceFilter],
    queryFn: () =>
      fetchCorrectiveActions({
        search: search.trim() || undefined,
        sourceType: sourceFilter === "all" ? undefined : sourceFilter,
        limit: 200,
      }),
  });

  const rows = useMemo(() => {
    const loaded = listQuery.data?.rows ?? [];
    if (statusFilter === "all") return loaded;
    if (statusFilter === "open_workflow") {
      return loaded.filter((row) => OPEN_WORKFLOW_STATUSES.has(row.status));
    }
    return loaded.filter((row) => row.status === statusFilter);
  }, [listQuery.data?.rows, statusFilter]);

  const filtersActive =
    search.trim() !== "" || statusFilter !== "all" || sourceFilter !== "all";

  const createMutation = useMutation({
    mutationFn: createCorrectiveAction,
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: CORRECTIVE_ACTIONS_LIST_KEY });
      setShowCreate(false);
      setCreateTitle("");
      setCreateProblem("");
      setCreateContainment("");
      setCreateTargetCloseDate("");
      setCreateSalesOrderId(null);
      setCreateQualityIncidentId("");
      showToast({ tone: "success", message: `Opened ${created.carNumber}.` });
      void navigate(`/quality/corrective-actions/${created.id}`);
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT}>Corrective actions</PageTitleWithHelp>
        {writesAllowed ? (
          <Button onClick={() => setShowCreate((open) => !open)}>
            <Plus className="mr-2 h-4 w-4" />
            New CAR
          </Button>
        ) : null}
      </header>

      {showCreate && writesAllowed ? (
        <Card>
          <CardHeader>
            <CardTitle>New corrective action report</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="car-title">Title</Label>
                <Input
                  id="car-title"
                  value={createTitle}
                  onChange={(event) => setCreateTitle(event.target.value)}
                  placeholder="Short summary of the nonconformity"
                />
              </div>
              <div>
                <Label htmlFor="car-target-close">Target close date</Label>
                <Input
                  id="car-target-close"
                  type="date"
                  value={createTargetCloseDate}
                  onChange={(event) => setCreateTargetCloseDate(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="car-source">Source</Label>
                <select
                  id="car-source"
                  className={cn(
                    "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                    "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
                  )}
                  value={createSource}
                  onChange={(event) => setCreateSource(event.target.value as CorrectiveActionSource)}
                >
                  {CORRECTIVE_ACTION_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {correctiveActionSourceLabel(source)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="car-sales-order">Sales order (optional)</Label>
                <QualitySalesOrderPicker
                  id="car-sales-order"
                  values={createSalesOrderId == null ? [] : [createSalesOrderId]}
                  onChange={(ids) => setCreateSalesOrderId(ids[ids.length - 1] ?? null)}
                />
              </div>
              <div>
                <Label htmlFor="car-qc-incident-id">QC incident id (optional)</Label>
                <Input
                  id="car-qc-incident-id"
                  value={createQualityIncidentId}
                  onChange={(event) => setCreateQualityIncidentId(event.target.value)}
                  placeholder="Link an existing QC incident"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="car-problem">Problem description</Label>
              <TextArea
                id="car-problem"
                value={createProblem}
                onChange={(event) => setCreateProblem(event.target.value)}
                rows={4}
                placeholder="What happened, where discovered, and customer or production impact"
              />
            </div>
            <div>
              <Label htmlFor="car-containment">Immediate containment (optional)</Label>
              <TextArea
                id="car-containment"
                value={createContainment}
                onChange={(event) => setCreateContainment(event.target.value)}
                rows={3}
                placeholder="Quarantine, stop-ship, notify customer, or other immediate controls"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
              <Button
                disabled={createMutation.isPending || createTitle.trim() === ""}
                onClick={() => {
                  const qualityIncidentId =
                    createQualityIncidentId.trim() === "" ? undefined : Number(createQualityIncidentId);
                  createMutation.mutate({
                    title: createTitle.trim(),
                    sourceType: createSource,
                    problemDescription: createProblem.trim() || undefined,
                    immediateContainment: createContainment.trim() || undefined,
                    targetCloseDate: createTargetCloseDate || undefined,
                    salesOrderId: createSalesOrderId ?? undefined,
                    qualityIncidentId: Number.isFinite(qualityIncidentId) ? qualityIncidentId : undefined,
                  });
                }}
              >
                {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Create CAR
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-3">
          <div className="md:col-span-2">
            <Label htmlFor="car-search">Search corrective actions</Label>
            <Input
              id="car-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by CAR number, title, or problem text"
            />
          </div>
          <div>
            <Label htmlFor="car-source-filter">Source</Label>
            <select
              id="car-source-filter"
              className={cn(
                "mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
              )}
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value as CorrectiveActionSource | "all")}
            >
              <option value="all">All sources</option>
              {CORRECTIVE_ACTION_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {correctiveActionSourceLabel(source)}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <p className="mb-2 text-xs font-medium text-slate-600">Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUS_FILTER_OPTIONS.map((option) => (
                <Button
                  key={option.key}
                  size="sm"
                  variant={statusFilter === option.key ? "primary" : "secondary"}
                  onClick={() => setStatusFilter(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      <HubDataTable
        title={
          listQuery.isLoading
            ? "Loading corrective actions…"
            : `${rows.length} corrective action report${rows.length === 1 ? "" : "s"}`
        }
        columns={CAR_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        loading={listQuery.isLoading}
        loadingMessage="Loading corrective actions…"
        error={listQuery.isError ? (listQuery.error as Error).message : null}
        empty="No corrective action reports yet."
        isFiltered={filtersActive}
        filteredEmpty="No corrective action reports match these filters."
      />
    </div>
  );
}
